import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EventDetail, EventSummary, EvidenceSpan } from '@finder/contracts';
import { ApiClient, apiUrl } from '../api-client.js';
import { trackOutboundClick } from '../lib/analytics.js';
import { Icon } from './Icon.js';
import {
  distanceLabel,
  leadDescription,
  locationPrecisionLabel,
  readableTeaser,
} from './format.js';
import { evidenceKindLabel } from './evidence.js';
import { OfferBadges } from './OfferBadges.js';
import { CATEGORY_LABEL, deriveCategory } from './category.js';
import { shortPlace, streetAddress } from './short-place.js';

const api = new ApiClient();

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; detail: EventDetail }
  | { status: 'error' };

/**
 * In-app evidence view for one event (issue #11). Shows the full description
 * and the food/attendance quotes from `EventDetail.foodEvidence` so a visitor
 * can see why the event qualifies without leaving the results list.
 *
 * Focus: on open, focus moves to the dialog; Tab cycles inside it; Escape or
 * the close button returns focus to whatever opened it. The rest of the page
 * is marked `inert` while the dialog is open.
 */
export function EventDetailDialog({
  event,
  origin,
  onClose,
  presentation = 'sheet',
  distanceVoice = 'place',
}: {
  event: EventSummary;
  origin: { lat: number; lng: number };
  onClose: () => void;
  distanceVoice?: 'gps' | 'place';
  /** Sheet is the phone dialog. Pane is the laptop column and is not modal. */
  presentation?: 'sheet' | 'pane';
}): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [showAllQuotes, setShowAllQuotes] = useState(false);
  const titleId = `detail-title-${event.canonicalId}`;

  useEffect(() => {
    let cancelled = false;
    setShowAllQuotes(false);
    setState({ status: 'loading' });
    void api
      .getEvent(event.canonicalId, origin)
      .then((detail) => {
        if (!cancelled) setState({ status: 'ready', detail });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [event.canonicalId, origin.lat, origin.lng, attempt]);

  useEffect(() => {
    if (presentation === 'pane') return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.focus();
    const root = document.getElementById('root');
    root?.setAttribute('inert', '');
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === 'Escape') {
        keyEvent.preventDefault();
        onCloseRef.current();
        return;
      }
      if (keyEvent.key !== 'Tab') return;
      const focusable = focusableElements(dialog);
      if (focusable.length === 0) {
        keyEvent.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (keyEvent.shiftKey) {
        if (active === first || active === dialog || !dialog.contains(active)) {
          keyEvent.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        keyEvent.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      root?.removeAttribute('inert');
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus();
    };
  }, [presentation]);

  const detail = state.status === 'ready' ? state.detail : null;
  const foodQuotes = detail?.foodEvidence.filter((span) => span.kind === 'food_drink') ?? [];
  const entryQuotes =
    detail?.foodEvidence.filter(
      (span) => span.kind === 'free_attendance' || span.kind === 'public_access',
    ) ?? [];
  const offers = detail?.offers ?? event.offers;
  const foodState = detail?.foodState ?? event.foodState;
  const when = formatWhen(
    detail?.startInstant ?? event.startInstant,
    detail?.endInstant ?? event.endInstant,
    detail?.venueTimeZone ?? event.venueTimeZone,
  );
  const lead = detail ? leadDescription(detail.description) : readableTeaser(event.teaser);
  const category = deriveCategory({
    title: event.title,
    teaser: event.teaser,
    sourceNames: event.sourceNames,
    primarySourceName: event.primarySourceLink.sourceName,
    audienceTags: event.audienceTags,
  });
  const precision = detail?.locationPrecision ?? event.locationPrecision;
  const miles = detail ? detail.visitor?.distanceMiles ?? null : event.distanceMiles;
  const distance = distanceLabel(miles, distanceVoice);
  const precisionLabel = locationPrecisionLabel(precision);
  const venueName = detail?.venueName ?? event.venueName;
  const venue = shortPlace(venueName);
  const approximate = precision === 'approximate' || precision === 'unknown';
  const street = approximate
    ? "The source didn't give an exact address."
    : streetAddress(venueName) ?? 'No street address';
  const coords = detail?.coordinates ?? event.coordinates;
  const directionsHref = coords
    ? `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`
    : null;
  const sourceLinks = detail?.sourceLinks ?? [];
  const primaryHref = apiUrl((detail ?? event).primarySourceLink.href);
  const trackSource = (url: string) => {
    trackOutboundClick({
      eventId: event.canonicalId,
      eventTitle: detail?.title ?? event.title,
      url,
    });
  };

  const content = (
    <>
      <div className="detail-toolbar">
        <p className="visually-hidden" role="status">
          {state.status === 'loading'
            ? 'Loading event details'
            : state.status === 'error'
              ? 'Could not load event details'
              : 'Event details loaded'}
        </p>
        <button type="button" className="detail-close" onClick={() => onCloseRef.current()}>
          <Icon name="x" size={16} />
          Close
        </button>
      </div>

      <p className={`detail-hero detail-hero--${category}`}>
        <span className="detail-hero__kicker">Category</span>
        <span className="detail-hero__name">{CATEGORY_LABEL[category]}</span>
      </p>
      <h2 id={titleId} className="detail-title">
        {detail?.title ?? event.title}
      </h2>
      <p className="detail-when">{when}</p>
      <p className="place-row">
        <Icon name="pin" size={15} />
        {venue ? <span>{venue}</span> : null}
        <span className="distance-pill">{distance}</span>
        {precisionLabel ? <span className="precision-pill">{precisionLabel}</span> : null}
      </p>
      <p className="place-street">{street}</p>
      {directionsHref ? (
        <a
          className="detail-directions"
          href={directionsHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {approximate ? 'Approximate map point' : 'Directions'}
          <span className="visually-hidden"> (opens in a new tab)</span>
        </a>
      ) : null}
      <OfferBadges event={{ offers, foodState }} />
      {lead ? <p className="detail-description">{lead}</p> : null}
      <a
        className="cta"
        href={primaryHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackSource(primaryHref)}
      >
        Open original page
        <span className="visually-hidden"> (opens in a new tab)</span>
        <Icon name="arrow" size={16} />
      </a>
      {sourceLinks.length > 1 ? (
        <section className="detail-section" aria-labelledby={`${titleId}-sources`}>
          <h3 id={`${titleId}-sources`}>Also listed on</h3>
          <ul className="detail-sources">
            {sourceLinks.map((link) => {
              const href = apiUrl(link.href);
              return (
                <li key={link.sourceLinkId}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackSource(href)}
                  >
                    {link.sourceName}
                    <span className="visually-hidden"> (opens in a new tab)</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      ) : (
        <p className="trust-footer">
          <Icon name="shield" size={14} />
          <span>
            via <strong>{(detail ?? event).primarySourceLink.sourceName}</strong>
          </span>
        </p>
      )}
      {state.status === 'loading' ? (
        <p className="detail-status">Loading the source text…</p>
      ) : null}
      {state.status === 'error' ? (
        <p className="detail-status" role="alert">
          The full description could not be loaded.{' '}
          <button type="button" className="detail-retry" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : null}
      <WhyListed
        titleId={titleId}
        quotes={[...foodQuotes, ...entryQuotes]}
        ready={state.status === 'ready'}
        showAll={showAllQuotes}
        onToggle={() => setShowAllQuotes((open) => !open)}
      />
    </>
  );

  if (presentation === 'pane') {
    return (
      <article className="detail-pane" aria-labelledby={titleId}>
        {content}
      </article>
    );
  }

  return createPortal(
    <div
      className="detail-backdrop"
      onMouseDown={(mouseEvent) => {
        if (mouseEvent.target === mouseEvent.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={dialogRef}
        className="detail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="detail-scroll">{content}</div>
      </div>
    </div>,
    document.body,
  );
}

function WhyListed({
  titleId,
  quotes,
  ready,
  showAll,
  onToggle,
}: {
  titleId: string;
  quotes: EvidenceSpan[];
  ready: boolean;
  showAll: boolean;
  onToggle: () => void;
}): JSX.Element {
  const visible = showAll ? quotes : quotes.slice(0, 2);
  const extra = quotes.length > 2;
  return (
    <section className="why-listed" aria-labelledby={`${titleId}-why`}>
      <h3 id={`${titleId}-why`}>Why it&apos;s listed</h3>
      <p className="detail-lead">Free to attend, and the source mentions free food or drink.</p>
      {visible.length > 0 ? (
        <ul className="evidence-list" aria-label="why this event qualifies">
          {visible.map((span) => (
            <li key={`${span.kind}-${span.start}-${span.quote}`}>
              <figure className="evidence-quote">
                <blockquote>“{span.quote}”</blockquote>
                <figcaption>{evidenceKindLabel(span.kind)}</figcaption>
              </figure>
            </li>
          ))}
        </ul>
      ) : ready ? (
        <p className="detail-status">
          No separate food quote was found. The description above is the text that was classified.
        </p>
      ) : null}
      {extra ? (
        <button type="button" className="why-more" aria-expanded={showAll} onClick={onToggle}>
          {showAll ? 'Hide the other quotes' : 'Show the other quotes'}
          <Icon name="chevron" size={16} />
        </button>
      ) : null}
    </section>
  );
}

function focusableElements(root: HTMLElement): HTMLElement[] {
  const sel =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
  return [...root.querySelectorAll<HTMLElement>(sel)].filter((el) => el.tabIndex >= 0);
}

function formatWhen(start: string, end: string, timeZone: string): string {
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const date = startDate.toLocaleString('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    const endTime = endDate.toLocaleString('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${date} – ${endTime}`;
  } catch {
    return start;
  }
}
